import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import PostcardCanvas from "@/components/PostcardCanvas";
import CardProgressMeter from "@/components/CardProgressMeter";
import { Button } from "@/components/ui/button";
import { Loader2, Mail, Users, Repeat, Maximize2, Bot, ShieldCheck, Home, ArrowRight, MapPin, Flame, Download } from "lucide-react";

const BENEFITS = [
  { Icon: Users, title: "Shared cost, premium presence", text: "The co-op splits the price of a full 9×12 premium mailer across a handful of local businesses — so you get big-format reach for a fraction of what solo direct mail costs." },
  { Icon: Home, title: "10,000 homes, every drop", text: "Guaranteed delivery to every mailbox in the ZIP — not an impression, not an algorithm. Real households, fully saturated." },
  { Icon: Repeat, title: "Repetition is what converts", text: "Direct mail rewards consistency. Monthly drops keep you top-of-mind; a 12-month commitment compounds recognition and response." },
  { Icon: Maximize2, title: "Premium 9×12 beats a 4×6", text: "The largest standard mail format. It doesn't get lost in the pile — it is the pile. Impossible to toss without seeing your offer." },
  { Icon: Bot, title: "Online listing + AI, included", text: "Every ad space bundles your Saving America Money listing, so shoppers keep finding you through AI assistants and local search long after the mail lands." },
  { Icon: ShieldCheck, title: "Trusted local company", text: "You appear alongside other vetted neighborhood businesses, borrowing the credibility of a curated, community-first mailer." },
];

const QUAD_META = [
  { key: "TL", label: "Top Left" },
  { key: "TR", label: "Top Right" },
  { key: "BL", label: "Bottom Left" },
  { key: "BR", label: "Bottom Right" },
];

function RouteMap({ route, zip }) {
  if (!route) return null;
  const byKey = Object.fromEntries((route.quadrants || []).map((q) => [q.key, q]));
  const cur = route.current?.quadrant;
  const nxt = route.next?.quadrant;
  const cell = ({ key, label }) => {
    const q = byKey[key] || {};
    const isCur = cur === key, isNext = nxt === key;
    return (
      <div key={key} data-testid={`route-quad-${key}`}
        className={`relative rounded-lg p-3 flex flex-col justify-between min-h-[104px] border-2 transition-colors
          ${isCur ? "bg-sam-crimson text-white border-sam-crimson shadow-lg" : isNext ? "bg-sam-amber/20 border-sam-amber text-sam-navy" : "bg-slate-50 border-slate-200 text-sam-slate"}`}>
        <div className="flex items-center justify-between">
          <span className="text-[10px] font-bold uppercase tracking-wide">{label}</span>
          <span className="text-[9px] font-mono opacity-70">{key}</span>
        </div>
        {isCur && <span className="text-[10px] font-black uppercase tracking-wide">Mailing now</span>}
        {isNext && <span className="text-[10px] font-black uppercase tracking-wide text-sam-amber">Up next</span>}
        <div>
          <div className="font-display font-black text-lg leading-none">{(q.households || 0).toLocaleString()}</div>
          <div className="text-[9px] uppercase tracking-wide opacity-70">homes</div>
        </div>
      </div>
    );
  };
  return (
    <div className="mt-14" data-testid="route-map-section">
      <h2 className="font-display text-2xl font-black text-sam-navy text-center">Where your ad lands — and when</h2>
      <p className="text-sam-slate text-center mt-2 text-sm max-w-2xl mx-auto">
        Your route centers on ZIP {zip}. Each month we saturate one quadrant of the radius, rotating
        <span className="font-semibold text-sam-navy"> Top Right → Top Left → Bottom Left → Bottom Right</span> — so every home sees your card three times a year without a monthly overload.
      </p>
      <div className="mt-8 grid md:grid-cols-[1fr_320px] gap-8 items-center max-w-3xl mx-auto">
        <div className="relative" data-testid="route-map">
          <div className="grid grid-cols-2 gap-2">{QUAD_META.map(cell)}</div>
          <div className="absolute inset-0 grid place-items-center pointer-events-none">
            <div className="rounded-full bg-sam-navy text-sam-cream h-16 w-16 grid place-items-center text-center shadow-lg border-4 border-white">
              <div><div className="text-[9px] uppercase opacity-70 leading-none">ZIP</div><div className="font-display font-black text-sm leading-tight">{zip}</div></div>
            </div>
          </div>
        </div>
        <div className="space-y-3 text-sm" data-testid="route-drop-info">
          <div className="rounded-xl border border-border bg-card p-4">
            <div className="text-xs font-bold uppercase tracking-widest text-sam-crimson">Mailing this month</div>
            <div className="mt-1 font-display font-black text-sam-navy text-lg" data-testid="route-current-drop">{route.current?.month} · {route.current?.label}</div>
          </div>
          <div className="rounded-xl border border-border bg-card p-4">
            <div className="text-xs font-bold uppercase tracking-widest text-sam-amber">Next drop</div>
            <div className="mt-1 font-display font-black text-sam-navy text-lg" data-testid="route-next-drop">{route.next?.month} · {route.next?.label}</div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-xl bg-sam-navy text-sam-cream p-4 text-center">
              <div className="font-display text-xl font-black text-sam-amber" data-testid="route-total-households">{(route.total_households || 0).toLocaleString()}</div>
              <div className="text-[10px] uppercase tracking-wide text-sam-cream/70 mt-0.5">total homes</div>
            </div>
            <div className="rounded-xl bg-sam-navy text-sam-cream p-4 text-center">
              <div className="font-display text-xl font-black text-sam-amber">{route.radius_miles ?? "—"} mi</div>
              <div className="text-[10px] uppercase tracking-wide text-sam-cream/70 mt-0.5">radius</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ZipPage() {
  const { state, city, zip } = useParams();
  const API_BASE = process.env.REACT_APP_BACKEND_URL;
  const navigate = useNavigate();
  const [page, setPage] = useState(null);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    api.get(`/zip-pages/${state}/${city}/${zip}`)
      .then(({ data }) => { setPage(data); setStatus("ok"); })
      .catch(() => setStatus("notfound"));
  }, [state, city, zip]);

  useEffect(() => {
    if (!page) return;
    document.title = `${page.headline || `Advertise to 10,000 ${page.city} homes`} | Saving America Money`;
    const desc = `Premium co-op postcard mailer for ${page.city}, ${page.state_abbr} ${page.zip}. Reach 10,000 local homes on a full 9×12 mailer — online listing + AI directory included.`;
    let m = document.querySelector('meta[name="description"]');
    if (!m) { m = document.createElement("meta"); m.name = "description"; document.head.appendChild(m); }
    m.content = desc;
    return () => { document.title = "Saving America Money"; };
  }, [page]);

  if (status === "loading") return <div className="h-[60vh] grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-sam-crimson" /></div>;
  if (status === "notfound") return (
    <div className="mx-auto max-w-xl px-6 py-24 text-center" data-testid="zip-notfound">
      <h1 className="font-display text-3xl font-black text-sam-navy">Page not found</h1>
      <p className="text-sam-slate mt-3">This mailer page isn't available yet.</p>
      <Button asChild className="mt-6 bg-sam-crimson hover:bg-sam-crimson/90"><Link to="/plans">See plans &amp; pricing</Link></Button>
    </div>
  );

  const cov = page.coverage || {};
  const remaining = (cov.total_cap ?? 12) - (cov.total_booked ?? 0);
  const totalCap = cov.total_cap ?? 12;
  const fillPct = cov.total_cap ? Math.round((cov.total_booked || 0) / cov.total_cap * 100) : 0;
  const fillingFast = fillPct >= 67 && remaining > 0;
  const planUrl = `/mailer?zip=${page.zip}&city=${encodeURIComponent(page.city)}&state=${page.state_abbr}`;
  const shareUrl = typeof window !== "undefined" ? window.location.href : "";
  const mailto = `mailto:?subject=${encodeURIComponent(`Your spot on the ${page.city} postcard mailer`)}&body=${encodeURIComponent(`Take a look at the mailer proof and live availability for ${page.city}, ${page.state_abbr} ${page.zip}:\n\n${shareUrl}\n\nOnly ${remaining} of ${totalCap} ad spaces remain on the co-op premium 9×12 mailer.`)}`;

  return (
    <div data-testid="zip-page">
      <div className="bg-gradient-to-br from-sam-navy to-sam-navy2 text-sam-cream">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 py-14">
          <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-sam-amber"><Mail className="h-4 w-4" />Premium Co-op Postcard Mailer</span>
          <h1 className="mt-3 font-display text-4xl sm:text-5xl font-black">{page.headline || `Get your business in front of 10,000 ${page.city} homes`}</h1>
          <p className="mt-4 text-sam-cream/75 max-w-2xl text-base flex items-center gap-2"><MapPin className="h-4 w-4 text-sam-amber" />{page.city}, {page.state_abbr} · ZIP {page.zip}</p>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <Button asChild size="lg" className="bg-sam-crimson hover:bg-sam-crimson/90 font-semibold" data-testid="zip-advertise-btn"><Link to={planUrl}>Advertise in this ZIP <ArrowRight className="h-4 w-4 ml-1.5" /></Link></Button>
            <Button asChild size="lg" variant="outline" className="border-sam-cream/30 bg-white/5 text-sam-cream hover:bg-white/10 hover:text-sam-cream font-semibold" data-testid="zip-email-btn"><a href={mailto}><Mail className="h-4 w-4 mr-2" />Email this proof</a></Button>
            <div className="inline-flex items-center rounded-full bg-white/10 ring-1 ring-white/15 px-4 py-2 text-sm font-semibold" data-testid="zip-availability">{remaining} of {totalCap} ad spaces open</div>
            {fillingFast && <span className="inline-flex items-center gap-1.5 rounded-full bg-sam-crimson text-white text-sm font-bold px-3.5 py-2 animate-pulse" data-testid="zip-filling-fast"><Flame className="h-4 w-4" />Filling fast</span>}
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-5xl px-4 sm:px-6 py-12">
        <h2 className="font-display text-2xl font-black text-sam-navy text-center">Your mailer — front &amp; back</h2>
        <p className="text-sam-slate text-center mt-2 text-sm">The actual 9×12 premium postcard mailed to every home in {page.zip} — updating live as spaces fill.</p>
        {page.run && page.run.slots ? (
          <div className="mt-8" data-testid="zip-live-card">
            {page.card_full && (
              <div className="mb-6 rounded-xl bg-sam-navy text-sam-cream p-5 text-center" data-testid="zip-card-full">
                <p className="font-display font-black text-xl">This drop is full 🎉</p>
                <p className="text-sam-cream/75 text-sm mt-1">All 12 spaces are sold. Join the waitlist for the next {page.city} drop.</p>
                <Button asChild className="mt-3 bg-sam-crimson hover:bg-sam-crimson/90" data-testid="zip-waitlist-btn"><Link to={planUrl}>Join the waitlist</Link></Button>
              </div>
            )}
            <CardProgressMeter p={page.mail_progress} />
            <PostcardCanvas slots={page.run.slots} run={page.run} selectable={!page.card_full} onSelect={() => navigate(planUrl)} />
            <div className="mt-4 text-center">
              <Button asChild variant="outline" data-testid="zip-proof-pdf">
                <a href={`${API_BASE}/api/postcard-runs/${page.run.id}/export.pdf?proof=1`} target="_blank" rel="noreferrer"><Download className="h-4 w-4 mr-2" />View postcard proof (PDF)</a>
              </Button>
            </div>
          </div>
        ) : (
          <div className="mt-8 rounded-xl border-2 border-dashed border-border grid place-items-center text-sam-slate text-sm h-48" data-testid="zip-proof-soon">
            The live mailer proof is being set up for this ZIP — check back soon.
          </div>
        )}
        {page.route && <RouteMap route={page.route} zip={page.zip} />}
      </div>

      <div className="bg-sam-cream/50 border-y border-border">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 py-14">
          <div className="text-center">
            <span className="text-xs font-bold uppercase tracking-widest text-sam-crimson">Why it works</span>
            <h2 className="mt-2 font-display text-3xl font-black text-sam-navy">Why the co-op premium mailer out-performs</h2>
            <p className="mt-3 text-sam-slate max-w-2xl mx-auto">Direct mail is still the highest-trust, highest-response local channel — and sharing a premium mailer makes it affordable.</p>
          </div>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {BENEFITS.map((b) => (
              <div key={b.title} className="rounded-2xl bg-card border border-border p-6">
                <div className="h-10 w-10 rounded-lg bg-sam-navy/10 grid place-items-center"><b.Icon className="h-5 w-5 text-sam-navy" /></div>
                <h3 className="mt-4 font-display font-bold text-sam-navy">{b.title}</h3>
                <p className="mt-1.5 text-sm text-sam-slate">{b.text}</p>
              </div>
            ))}
          </div>
          <div className="mt-10 grid grid-cols-3 gap-4 max-w-2xl mx-auto text-center">
            {[["10,000", "homes per drop"], ["9×12 in", "premium format"], ["Co-op", "low shared price"]].map(([n, l]) => (
              <div key={l} className="rounded-xl bg-sam-navy text-sam-cream p-5"><div className="font-display text-2xl sm:text-3xl font-black text-sam-amber">{n}</div><div className="text-xs mt-1 text-sam-cream/70 uppercase tracking-wide">{l}</div></div>
            ))}
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-3xl px-4 sm:px-6 py-14 text-center">
        {page.custom_note && <p className="text-sam-navy text-lg mb-8 whitespace-pre-line" data-testid="zip-custom-note">{page.custom_note}</p>}
        <h2 className="font-display text-3xl font-black text-sam-navy">Claim your spot in {page.city}</h2>
        <p className="text-sam-slate mt-3">Only {remaining} of {totalCap} ad spaces remain on the {page.zip} mailer.</p>
        <Button asChild size="lg" className="mt-6 bg-sam-crimson hover:bg-sam-crimson/90 font-semibold"><Link to={planUrl}>Advertise in this ZIP <ArrowRight className="h-4 w-4 ml-1.5" /></Link></Button>
      </div>
    </div>
  );
}
